import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IOperatorClass } from './operators/OperatorClass';
import { FileValidator } from './operators/FileValidator';
import { PrfCreator } from './operators/PrfCreator';
import { CompanyInfoOperator } from './operators/CompanyInfoOperator';
import { GroupReportingOperator } from './operators/GroupReportingOperator';
import { AnnualStatementOperator } from './operators/AnnualStatementOperator';
import { PremiumReportOperator } from './operators/PremiumReportOperator';
import { UserDefinedDataOperator } from './operators/UserDefinedDataOperator';
import { CommentsOperator } from './operators/CommentsOperator';
import { OperatorInput } from './operators/models/models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { CompanyService } from 'src/app/company/company.service';
import { UserDefinedColumnOperator } from './operators/UserDefinedColumnOperator';

interface UploadProgressStep {
  label: string;
  completed: boolean;
  operatorClass: IOperatorClass,
  message?: string[];
  status?: "error" | "success" | "warning" | "skipped";
  redirectUrl?: string;
}

@Component({
  selector: 'prf-template-uploader',
  templateUrl: './prf-template-upload-progress.component.html',
  styleUrls: ['./prf-template-upload-progress.component.scss'],
  standalone: false
})
export class PrfTemplateUploadProgressComponent implements OnInit, OnDestroy {
  @Input() operatorInput: OperatorInput;
  fileName = '';
  isSuccess = false;
  isDeletingPrf = false;


  currentStepIndex = 0;
  private timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;

  constructor(public activeModal: NgbActiveModal, private router: Router, private companyService: CompanyService) { }

  steps: UploadProgressStep[] = [
    { label: 'Validating File', completed: false, operatorClass: new FileValidator() },
    { label: 'Checking PRF Validity', completed: false, operatorClass: new PrfCreator() },
    { label: 'Company Information', completed: false, operatorClass: new CompanyInfoOperator(), redirectUrl: 'companyinformation' },
    { label: 'Group Reporting', completed: false, operatorClass: new GroupReportingOperator() , redirectUrl: 'groupreporting'},
    { label: 'Annual Statement', completed: false, operatorClass: new AnnualStatementOperator(), redirectUrl: 'annualstatement' },
    { label: 'Premium Report', completed: false, operatorClass: new PremiumReportOperator(), redirectUrl: 'premiumreport' },
    { label: 'User Defined Column', completed: false, operatorClass: new UserDefinedColumnOperator(), redirectUrl: 'userdefineddata' },
    { label: 'User Defined Data', completed: false, operatorClass: new UserDefinedDataOperator(), redirectUrl: 'userdefineddata' },
    { label: 'Comments', completed: false, operatorClass: new CommentsOperator(), redirectUrl: 'comments' }
  ];

  ngOnInit(): void {
    this.fileName = this.operatorInput?.file?.name || '';
    this.advanceStep();
  }

  ngOnDestroy(): void {
    this.clearPendingTimeout();
  }

  isCurrentStep(index: number): boolean {
    return index === this.currentStepIndex && !this.steps[index].completed;
  }

  getStepStatus(index: number): string {
    if (this.steps[index].completed) {
      if (this.steps[index].status === 'error') {
        return 'Error';
      }
      if (this.steps[index].status === 'skipped') {
        return 'Skipped';
      }
      return 'Completed';
    }

    if (this.isCurrentStep(index)) {
      return 'In Progress';
    }

    return 'Pending';
  }

  get completedStepsCount(): number {
    return this.steps.filter((step) => step.completed && step.status !== 'error').length;
  }

  get progressPercentage(): number {
    return Math.round((this.completedStepsCount / this.steps.length) * 100);
  }

  get isComplete(): boolean {
    return this.completedStepsCount === this.steps.length || this.steps[this.currentStepIndex].status === 'error';
  }

  deletePrf(): void {
    if (this.isDeletingPrf) {
      return;
    }

    this.isDeletingPrf = true;
    this.companyService.deleteCompany(this.operatorInput.prfyear, this.operatorInput.prfnumber, this.operatorInput.prfGroup, '').subscribe({
      next: () => {
        this.isDeletingPrf = false;
        this.activeModal.close('restart');
      },
      error: () => {
        this.isDeletingPrf = false;
      }
    });
  }

  private advanceStep(): void {
    if (this.currentStepIndex >= this.steps.length) {
      this.isSuccess = this.steps.every(step => step.status !== 'error');
      return;
    }

    this.timeoutId = globalThis.setTimeout(async () => {
      var output = await this.steps[this.currentStepIndex].operatorClass.runOperator(this.operatorInput);
      this.steps[this.currentStepIndex].completed = true;
      if (output) {
        this.steps[this.currentStepIndex].message = output?.message;
        this.steps[this.currentStepIndex].status = output?.status;
      }
      this.operatorInput = output ? output.operatorInput : this.operatorInput;
      if (this.steps[this.currentStepIndex].status != 'error') {
        this.currentStepIndex += 1;
        this.advanceStep();
      }
    }, 1200);
  }

  private clearPendingTimeout(): void {
    if (this.timeoutId !== null) {
      globalThis.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  redirectUrl(): string {
    if(this.currentStepIndex >= this.steps.length){
      return 'premiumreport';
    }
    return this.steps[this.currentStepIndex].redirectUrl ? this.steps[this.currentStepIndex].redirectUrl : 'companyinformation';

  }

  viewPrf(): void {
    this.router.navigate(
      ['/' +
        this.operatorInput.prfyear,
      this.operatorInput.prfnumber,
      this.redirectUrl()
      ]
    );
    this.activeModal.close();
  }

}
